import { useMemo } from 'react'
import { eventTone, type EventTone } from '@/components/eventTone'
import type { ShownEvent } from '@/hooks/useEvents'
import { usePeople } from '@/hooks/usePeople'
import { useSwipe } from '@/hooks/useSwipe'
import { monthCells } from '@/logic/calendar'
import { fmtDate, fmtMonthYear, startOfMonth } from '@/logic/dates'
import { eventsOnDay } from '@/logic/events'
import { itemsOnDay } from '@/logic/plan'
import { usePrefs } from '@/store/prefs'
import type { Now } from '@/logic/status'
import { useCategories } from '@/store/categories'
import { setCalendarDate } from '@/store/ui'
import type { DateStr, Task } from '@/types'
import { DayList } from './DayList'
import { categoryColor, nextPeriod, plural, prevPeriod } from './shared'
import './MonthPhone.css'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const TONE_ORDER: EventTone[] = ['p1', 'p2', 'both']

interface MonthPhoneProps {
  tasks: Task[]
  events: ShownEvent[]
  now: Now
  date: DateStr
}

/**
 * Month on a phone: a Monday-first grid with a dot for each task (hollow for a
 * future routine or repeat) and a bar for each person with an event, under a
 * legend. Today is ringed and the selected day filled. The selected day's
 * events and tasks are listed beneath.
 */
export function MonthPhone({ tasks, events, now, date }: MonthPhoneProps) {
  const today = now.date
  const people = usePeople()
  const byCategory = useCategories((s) => s.byId)
  const swipeRef = useSwipe(nextPeriod, prevPeriod)
  const month = startOfMonth(date)

  const cells = useMemo(() => monthCells(tasks, month, today, categoryColor(byCategory)), [tasks, month, today, byCategory])
  // one bar per person with an event that day, and the count for the day's label
  const eventMarks = useMemo(() => {
    const out = new Map<DateStr, { tones: EventTone[]; count: number }>()
    for (const c of cells) {
      if (!c.date) continue
      const onDay = eventsOnDay(events, c.date)
      const set = new Set(onDay.map((e) => eventTone(e.owner, people)))
      out.set(c.date, { tones: TONE_ORDER.filter((t) => set.has(t)), count: onDay.length })
    }
    return out
  }, [cells, events, people])
  const hideDone = usePrefs((s) => !!s.filter.hideDone)
  const items = useMemo(() => itemsOnDay(tasks, date, today, hideDone, true), [tasks, date, today, hideDone])
  const dayEvents = useMemo(() => eventsOnDay(events, date), [events, date])

  return (
    <div className="mphone">
      <section ref={swipeRef} className="mphone__card" aria-label={fmtMonthYear(month)}>
        <div className="mphone__weekdays" aria-hidden="true">
          {WEEKDAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="mphone__grid" role="group" aria-label="Days">
          {cells.map((c, i) => {
            if (!c.date) return <span key={`blank-${i}`} className="mphone__cell mphone__cell--blank" aria-hidden="true" />
            const d = c.date
            const { tones, count } = eventMarks.get(d) ?? { tones: [], count: 0 }
            const label = [fmtDate(d), d === today ? 'today' : null, c.count ? plural(c.count, 'task') : null, count ? plural(count, 'event') : null].filter(Boolean).join(', ')
            return (
              <button
                type="button"
                key={d}
                className={`mphone__cell ${d === today ? 'mphone__cell--today' : ''} ${d === date ? 'mphone__cell--on' : ''}`}
                aria-label={label}
                aria-pressed={d === date}
                onClick={() => setCalendarDate(d)}
              >
                <span className="mphone__num">{c.day}</span>
                <span className="mphone__dots" aria-hidden="true">
                  {c.marks.map((m, k) => (
                    <span key={k} className={`mphone__dot ${m.kind === 'faint' ? 'mphone__dot--faint' : ''}`} style={m.color ? ({ '--mc': m.color } as React.CSSProperties) : undefined} />
                  ))}
                </span>
                <span className="mphone__bars" aria-hidden="true">
                  {tones.map((t) => (
                    <span key={t} className={`mphone__bar ev--${t}`} />
                  ))}
                </span>
              </button>
            )
          })}
        </div>
        <div className="mphone__legend" aria-hidden="true">
          <span>
            <span className="mphone__dot" />
            Task
          </span>
          <span>
            <span className="mphone__bar ev--both" />
            Event
          </span>
          <span>
            <span className="mphone__dot mphone__dot--faint" />
            Future routine or repeat
          </span>
        </div>
      </section>

      <DayList date={date} now={now} items={items} events={dayEvents} people={people} />
    </div>
  )
}
